/**
 * Money arrives from the API as a decimal string ("1234.50") and is never
 * computed on the client. The string is handed to Intl untouched, so no
 * floating point conversion happens on the way to the screen.
 */
export interface FormatMoneyOptions {
  /** ISO 4217 code. Defaults to INR. */
  currency?: string;
  /** BCP 47 locale. Defaults to en-IN (lakh/crore grouping). */
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

const DECIMAL_STRING = /^-?\d+(\.\d+)?$/;

export const DEFAULT_MONEY_LOCALE = "en-IN";
export const DEFAULT_MONEY_CURRENCY = "INR";

/** Build a reusable formatter, e.g. per tenant `createMoneyFormatter({ currency: tenant.currency })`. */
export function createMoneyFormatter(options: FormatMoneyOptions = {}): (amount: string) => string {
  const formatter = new Intl.NumberFormat(options.locale ?? DEFAULT_MONEY_LOCALE, {
    style: "currency",
    currency: options.currency ?? DEFAULT_MONEY_CURRENCY,
    minimumFractionDigits: options.minimumFractionDigits ?? 2,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  });
  return (amount: string): string => {
    if (!DECIMAL_STRING.test(amount)) return amount;
    // The regex above guarantees the numeric-literal shape Intl accepts.
    return formatter.format(amount as Intl.StringNumericLiteral);
  };
}

/** Format a decimal string as currency. Malformed input is returned as-is rather than shown as NaN. */
export function formatMoney(amount: string, options: FormatMoneyOptions = {}): string {
  return createMoneyFormatter(options)(amount);
}
